import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TuneRounded, WarningAmberRounded } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, DocumentData } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { tuitionApi } from '../../../lib/api/tuition.api';
import { errorText } from '../../../lib/error-text';
import { formatMoney } from '../../../lib/i18n-format';

interface Installment {
  id: string;
  label: string;
  dueDate: string;
  amount: number;
  type: 'enrollment' | 'monthly';
}

interface PaymentAllocation {
  installmentId: string;
  amount: number;
}

interface AdjustPaymentDialogProps {
  open: boolean;
  payment: DocumentData | null;
  tenantId: string;
  studentName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AdjustPaymentDialog({
  open,
  payment,
  tenantId,
  studentName,
  onClose,
  onSuccess,
}: AdjustPaymentDialogProps) {
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [targetInstallmentId, setTargetInstallmentId] = useState<string>('');
  const [deductionAmount, setDeductionAmount] = useState<string>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allocations: PaymentAllocation[] = useMemo(
    () => (payment?.allocations as PaymentAllocation[] | undefined) ?? [],
    [payment],
  );

  // Reset state on open/close
  useEffect(() => {
    if (open) {
      setTargetInstallmentId('');
      setDeductionAmount('');
      setReason('');
      setError(null);
    }
  }, [open]);

  // Load tuition plan to resolve installment labels
  useEffect(() => {
    if (!open || !payment?.tuitionPlanId || !tenantId) return;
    let cancelled = false;
    setLoadingPlan(true);
    (async () => {
      try {
        const snap = await getDoc(
          doc(db, `tenants/${tenantId}/tuitionPlans/${payment.tuitionPlanId}`),
        );
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() as { installments?: Installment[] };
          setInstallments(data.installments ?? []);
        } else {
          setInstallments([]);
        }
      } catch {
        if (!cancelled) setInstallments([]);
      } finally {
        if (!cancelled) setLoadingPlan(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, payment?.tuitionPlanId, tenantId]);

  const currency: string = payment?.currency ?? 'CLP';
  const fmt = (n: number) => formatMoney(n, currency);

  const labelFor = (installmentId: string): string => {
    const inst = installments.find((i) => i.id === installmentId);
    return inst ? `${inst.label} (${inst.dueDate})` : installmentId;
  };

  const selectedAllocation = allocations.find((a) => a.installmentId === targetInstallmentId);
  const maxDeduction = selectedAllocation?.amount ?? 0;
  const parsedDeduction = Number(deductionAmount);
  const deductionValid =
    !isNaN(parsedDeduction) && parsedDeduction > 0 && parsedDeduction <= maxDeduction;
  const canSubmit =
    !!targetInstallmentId && deductionValid && reason.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!payment || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await tuitionApi.adjustPayment({
        tenantId,
        paymentId: payment.id,
        deductionAmount: parsedDeduction,
        targetInstallmentId,
        reason: reason.trim(),
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setError(errorText(e, 'Error al ajustar el pago'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!payment) return null;

  const previousAdjustment: number = payment.adjustmentAmount ?? 0;
  const originalAmount: number = payment.originalAmount ?? payment.amount;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <TuneRounded color="warning" />
          <span>Ajustar Pago — Deducir Monto</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info" icon={<WarningAmberRounded />}>
            Esta acción reduce el monto del pago verificado y libera la cuota asociada. Útil cuando
            el alumno sobrepagó una cuota y debe coordinarse devolución externa.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Alumno: <strong style={{ color: 'white' }}>{studentName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monto actual: <strong style={{ color: 'white' }}>{fmt(payment.amount)}</strong>
              </Typography>
              {previousAdjustment > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Monto original:{' '}
                    <strong style={{ color: 'white' }}>{fmt(originalAmount)}</strong>
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    Ya ajustado previamente: <strong>{fmt(previousAdjustment)}</strong>
                  </Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                Referencia: <strong style={{ color: 'white' }}>{payment.reference}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fecha pago: <strong style={{ color: 'white' }}>{payment.paymentDate}</strong>
              </Typography>
            </Stack>
          </Paper>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Distribución actual del pago
            </Typography>
            {loadingPlan ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Cargando cuotas del plan…
                </Typography>
              </Stack>
            ) : allocations.length === 0 ? (
              <Alert severity="warning">Este pago no tiene allocations registradas.</Alert>
            ) : (
              <Stack spacing={0.5}>
                {allocations.map((a) => (
                  <Stack
                    key={a.installmentId}
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="body2">{labelFor(a.installmentId)}</Typography>
                    <Chip size="small" label={fmt(a.amount)} />
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Cuota objetivo de la deducción *
            </Typography>
            <Select
              fullWidth
              size="small"
              value={targetInstallmentId}
              onChange={(e) => {
                setTargetInstallmentId(e.target.value);
                setDeductionAmount('');
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Selecciona una cuota
              </MenuItem>
              {allocations.map((a) => (
                <MenuItem key={a.installmentId} value={a.installmentId}>
                  {labelFor(a.installmentId)} — {fmt(a.amount)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Monto a deducir *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={deductionAmount}
              onChange={(e) => setDeductionAmount(e.target.value)}
              disabled={!targetInstallmentId}
              error={!!deductionAmount && !deductionValid}
              helperText={
                targetInstallmentId
                  ? `Máximo: ${fmt(maxDeduction)}`
                  : 'Selecciona primero una cuota'
              }
              InputProps={{
                inputProps: { min: 1, max: maxDeduction, step: 1 },
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Razón del ajuste *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Alumno se retira del programa. Se devuelve el excedente pagado correspondiente a cuota de Mayo 2026."
            />
          </Box>

          {targetInstallmentId && deductionValid && (
            <Alert severity="success">
              Resultado: la cuota <strong>{labelFor(targetInstallmentId)}</strong> volverá a estado
              pendiente por <strong>{fmt(parsedDeduction)}</strong>. El monto efectivo del pago
              pasará a <strong>{fmt(payment.amount - parsedDeduction)}</strong>.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} /> : <TuneRounded />}
        >
          Aplicar ajuste
        </Button>
      </DialogActions>
    </Dialog>
  );
}
